<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiSettingsCard from '@/components/pos-v2/ui/PosUiSettingsCard.vue';
  import {
    colorSwatches,
    selectedSwatch,
  } from '@/domain/design/color-swatches';
  import type { IColorSwatch } from '@/domain/design/color-swatches';
  import { swatchInk } from '@/domain/storefront/storefront-colors';

  /**
   * «Color» (Storefront V2 ADM-3): «Del logo», the six food hues, «Actual»
   * and «Otro color» (the native colour picker, live while it moves). Store
   * colours reach the DOM only as the `--swatch` / `--swatch-ink` variables.
   */
  const props = defineProps<{
    /** The draft brand, `#rrggbb`. */
    value: string;
    savedBrand: string;
    logoColor?: string;
  }>();
  const emit = defineEmits<{ (e: 'input', hex: string): void }>();

  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string) => String(proxy.$t(key));

  const swatches = computed(() =>
    colorSwatches({ logoColor: props.logoColor, savedBrand: props.savedBrand })
  );
  const selected = computed(() => selectedSwatch(swatches.value, props.value));

  function label(swatch: IColorSwatch): string {
    if (swatch.id === 'logo') return t('design.color.fromLogo');
    if (swatch.id === 'current') return t('design.color.current');
    return t(`design.color.hue.${swatch.id}`);
  }

  function swatchStyle(hex: string): Record<string, string> {
    return { '--swatch': hex, '--swatch-ink': swatchInk(hex) };
  }

  function onPick(event: Event) {
    const input = event.target;
    if (input instanceof HTMLInputElement) emit('input', input.value);
  }
</script>

<template>
  <PosUiSettingsCard
    class="v2-design-color-card"
    :title="t('design.color.title')"
    :description="t('design.color.description')"
  >
    <div
      class="swatch-grid"
      role="radiogroup"
      :aria-label="t('design.color.title')"
    >
      <button
        v-for="swatch in swatches"
        :key="swatch.id"
        type="button"
        role="radio"
        class="swatch"
        :class="{ 'is-selected': selected === swatch.id }"
        :aria-checked="selected === swatch.id ? 'true' : 'false'"
        :data-swatch="swatch.id"
        :style="swatchStyle(swatch.hex)"
        @click="emit('input', swatch.hex)"
      >
        <span class="swatch-circle">
          <PosUiIcon
            v-show="selected === swatch.id"
            class="swatch-check"
            name="check"
            :size="22"
          />
        </span>
        <span class="swatch-label">{{ label(swatch) }}</span>
      </button>
      <label
        class="swatch is-custom"
        :class="{ 'is-selected': selected === 'other' }"
        data-swatch="other"
        :style="selected === 'other' ? swatchStyle(value) : undefined"
      >
        <input
          type="color"
          class="swatch-input"
          :value="value"
          :aria-label="t('design.color.other')"
          @input="onPick"
        />
        <span
          class="swatch-circle"
          :class="{ 'is-empty': selected !== 'other' }"
        >
          <PosUiIcon
            class="swatch-check"
            :name="selected === 'other' ? 'check' : 'plus'"
            :size="22"
          />
        </span>
        <span class="swatch-label">{{ t('design.color.other') }}</span>
      </label>
    </div>
  </PosUiSettingsCard>
</template>

<style scoped lang="scss">
  .v2-design-color-card {
    .swatch-grid {
      @apply tw-grid tw-gap-3;
      grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
    }

    .swatch {
      @apply tw-relative tw-flex tw-min-w-0 tw-cursor-pointer tw-flex-col tw-items-center tw-border-0 tw-bg-transparent tw-p-0;
    }

    .swatch-circle {
      @apply tw-flex tw-h-11 tw-w-11 tw-flex-none tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-neutral-200;
      background-color: var(--swatch);

      &.is-empty {
        @apply tw-bg-neutral-100 tw-text-neutral-500;
      }
    }

    .swatch-check {
      color: var(--swatch-ink);
    }

    .is-empty .swatch-check {
      @apply tw-text-neutral-500;
    }

    .swatch-label {
      @apply tw-mt-1.5 tw-w-full tw-truncate tw-text-center tw-text-[11px] tw-leading-4 tw-text-neutral-600;
    }

    .swatch.is-selected {
      .swatch-circle {
        box-shadow: 0 0 0 2px rgb(var(--c-surface)),
          0 0 0 4px rgb(var(--c-neutral-900));
      }

      .swatch-label {
        @apply tw-font-semibold tw-text-neutral-900;
      }
    }

    // The native picker covers the whole «Otro color» swatch, invisible.
    .swatch-input {
      @apply tw-absolute tw-left-0 tw-top-0 tw-m-0 tw-h-full tw-w-full tw-cursor-pointer tw-border-0 tw-p-0 tw-opacity-0;
    }

    // After the selected ring: the focus ring wins on a focused swatch.
    .swatch:focus-visible {
      @apply tw-outline-none;

      .swatch-circle {
        @apply tw-ring-2 tw-ring-orange-500/40;
      }
    }

    .swatch-input:focus-visible ~ .swatch-circle {
      @apply tw-ring-2 tw-ring-orange-500/40;
    }
  }
</style>
